'use strict';

/**
 * BookmarksPage -- the full-page view of the caller's bookmarks + browsing history, rendered into
 * the main workspace (#Plansheet-Workspace) rather than the notebook rail. It reuses the same
 * per-user data the notebook rail loads (providers.NotebookAPI.loadNotebook -> AppData.Notebook),
 * but keeps its own render/view state under AppData.BookmarksPage so the rail notebook and this page
 * never fight over the same buffer.
 *
 * Two sections: Favorites (pinned pages, each removable) and History (the last pages visited). Both
 * carry a compact "how long ago" age (2h, 3d, 5mo, 1y) that is shrink-proof so it never wraps a row
 * at phone width. Template-driven per the Pict conventions: onBeforeRender derives the slots into
 * AppData, the templates iterate them with {~TS:~}, and every action mutates + re-shows.
 */

const libPictView = require('pict-view');

class PictViewBookmarksPage extends libPictView
{
	_notebook() { return this.pict.AppData.Notebook || {}; }
	_page() { return this.pict.AppData.BookmarksPage || (this.pict.AppData.BookmarksPage = {}); }
	_api() { return this.pict.providers.NotebookAPI; }

	// Load (or refresh) the caller's notebook data, then repaint the page.
	show()
	{
		let tmpAPI = this._api();
		if (!tmpAPI || typeof tmpAPI.loadNotebook !== 'function') { this.render(); return; }
		return tmpAPI.loadNotebook().then(() => this.render()).catch(() => this.render());
	}

	// Parse a server timestamp to epoch ms (best effort).
	_ms(pValue)
	{
		if (!pValue) { return 0; }
		let tmpMs = Date.parse(String(pValue).replace(' ', 'T'));
		return isNaN(tmpMs) ? 0 : tmpMs;
	}

	// A compact "how long ago" label (now, 5m, 3h, 2d, 4mo, 1y). Short by design so it never forces
	// the row to wrap at narrow / phone width.
	_ago(pValue)
	{
		let tmpMs = this._ms(pValue);
		if (!tmpMs) { return ''; }
		let tmpSec = Math.max(0, Math.floor((Date.now() - tmpMs) / 1000));
		if (tmpSec < 60) { return 'now'; }
		let tmpMin = Math.floor(tmpSec / 60);
		if (tmpMin < 60) { return tmpMin + 'm'; }
		let tmpHr = Math.floor(tmpMin / 60);
		if (tmpHr < 24) { return tmpHr + 'h'; }
		let tmpDay = Math.floor(tmpHr / 24);
		if (tmpDay < 30) { return tmpDay + 'd'; }
		let tmpMon = Math.floor(tmpDay / 30);
		if (tmpMon < 12) { return tmpMon + 'mo'; }
		return Math.floor(tmpMon / 12) + 'y';
	}

	onBeforeRender(pRenderable)
	{
		let tmpNB = this._notebook();
		let tmpPage = this._page();

		tmpPage.FavRows = (tmpNB.Favorites || []).map((pB) => ({ IDBookmark: pB.IDBookmark, Label: pB.Label, Route: pB.Route, Age: this._ago(pB.CreateDate) }));
		tmpPage.RecentRows = (tmpNB.Recent || []).slice(0, 10).map((pR) => ({ Route: pR.Route, Label: pR.Label, Age: this._ago(pR.ViewedAt), CountLabel: pR.Count > 0 ? String(pR.Count) : '' }));
		// Trending mirrors the notebook rail: the caller's most-visited pages in the window, with a visit count.
		tmpPage.TrendRows = (tmpNB.Trending || []).slice(0, 10).map((pT) => ({ Route: pT.Route, Label: pT.Label, Count: pT.Count }));

		// One-or-zero-element slots drive the section body vs. its empty state (the pict conditional pattern).
		tmpPage.FavSlot = tmpPage.FavRows.length ? [1] : [];
		tmpPage.FavEmpty = tmpPage.FavRows.length ? [] : [1];
		tmpPage.RecentSlot = tmpPage.RecentRows.length ? [1] : [];
		tmpPage.RecentEmpty = tmpPage.RecentRows.length ? [] : [1];
		tmpPage.TrendSlot = tmpPage.TrendRows.length ? [1] : [];
		tmpPage.TrendEmpty = tmpPage.TrendRows.length ? [] : [1];

		return super.onBeforeRender(pRenderable);
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		if (this.pict.CSSMap) { this.pict.CSSMap.injectCSS(); }
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}

	go(pRoute)
	{
		if (typeof window === 'undefined') { return; }
		window.location.hash = '#' + String(pRoute || '/').replace(/^#/, '');
	}

	removeBookmark(pID)
	{
		let tmpAPI = this._api();
		if (!tmpAPI || typeof tmpAPI.deleteBookmark !== 'function') { return; }
		tmpAPI.deleteBookmark(pID).then(() => this.show()).catch(() => {});
	}
}

module.exports = PictViewBookmarksPage;

module.exports.default_configuration =
{
	ViewIdentifier: 'Plansheet-BookmarksPage',
	DefaultRenderable: 'Plansheet-BookmarksPage',
	DefaultDestinationAddress: '#Plansheet-Workspace',
	AutoRender: false,
	CSS: /*css*/`
		.ps-bmpage.ps-page { color: var(--theme-color-text-primary, #1d2230); }
		.ps-bmpage-head { display: flex; align-items: center; gap: 12px; margin: 0 0 18px; }
		.ps-bmpage-head h2 { font-size: 22px; font-weight: 600; margin: 0; color: var(--theme-color-text-primary, #1f2430); }
		.ps-bmpage-section { margin: 0 0 26px; }
		.ps-bmpage-section-title { font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em;
			color: var(--theme-color-text-secondary, #8a93a6); margin: 0 2px 8px; }
		.ps-bmpage-list { display: flex; flex-direction: column; gap: 2px; }
		.ps-bmpage-row { display: flex; align-items: center; gap: 8px; padding: 7px 10px; border-radius: 8px; }
		.ps-bmpage-row:hover { background: var(--theme-color-background-hover, #eef1f6); }
		.ps-bmpage-link { flex: 1 1 auto; min-width: 0; font-size: 14px; cursor: pointer;
			color: var(--theme-color-brand-primary, #3b6fd4); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
		.ps-bmpage-link:hover { text-decoration: underline; }
		/* Compact age -- fixed, tiny, tabular, shrink-proof so it never wraps a row on a phone. */
		.ps-bmpage-age { flex: 0 0 auto; font-size: 10px; color: var(--theme-color-text-secondary, #8a93a6); font-variant-numeric: tabular-nums; white-space: nowrap; }
		/* Trending visit count -- a small pill, same shrink-proof footprint as the age. */
		.ps-bmpage-count { flex: 0 0 auto; min-width: 20px; text-align: center; font-size: 10px; font-weight: 600; line-height: 16px;
			padding: 0 6px; border-radius: 9px; color: var(--theme-color-text-secondary, #8a93a6);
			background: var(--theme-color-background-hover, #eef1f6); font-variant-numeric: tabular-nums; white-space: nowrap; }
		/* History visit-count pill: hidden when CountLabel is '' (page not visited within the trending window). */
		.ps-bmpage-count-recent:empty { display: none; }
		.ps-bmpage-x { flex: 0 0 auto; width: 24px; height: 24px; border: none; border-radius: 6px; background: transparent;
			color: var(--theme-color-text-secondary, #8a93a6); cursor: pointer; font-size: 16px; line-height: 1;
			display: flex; align-items: center; justify-content: center; }
		.ps-bmpage-x:hover { background: var(--theme-color-background-hover, #e0e5ee); color: var(--theme-color-status-error, #cc3333); }
		.ps-bmpage-empty { color: var(--theme-color-text-secondary, #8a93a6); font-style: italic; font-size: 14px; padding: 8px 2px; }
	`,
	Templates:
	[
		{
			Hash: 'Plansheet-BookmarksPage',
			Template: /*html*/`
<div class="ps-page ps-bmpage">
	<div class="ps-bmpage-head">
		<h2>Bookmarks</h2>
	</div>
	<div class="ps-bmpage-section">
		<div class="ps-bmpage-section-title">Favorites</div>
		{~TS:BookmarksPage-FavList:AppData.BookmarksPage.FavSlot~}
		{~TS:BookmarksPage-FavEmpty:AppData.BookmarksPage.FavEmpty~}
	</div>
	<div class="ps-bmpage-section">
		<div class="ps-bmpage-section-title">History</div>
		{~TS:BookmarksPage-RecentList:AppData.BookmarksPage.RecentSlot~}
		{~TS:BookmarksPage-RecentEmpty:AppData.BookmarksPage.RecentEmpty~}
	</div>
	<div class="ps-bmpage-section">
		<div class="ps-bmpage-section-title">Trending</div>
		{~TS:BookmarksPage-TrendList:AppData.BookmarksPage.TrendSlot~}
		{~TS:BookmarksPage-TrendEmpty:AppData.BookmarksPage.TrendEmpty~}
	</div>
</div>`
		},
		{
			Hash: 'BookmarksPage-FavList',
			Template: /*html*/`<div class="ps-bmpage-list">{~TS:BookmarksPage-FavRow:AppData.BookmarksPage.FavRows~}</div>`
		},
		{
			Hash: 'BookmarksPage-FavRow',
			Template: /*html*/`<div class="ps-bmpage-row"><a class="ps-bmpage-link" onclick="_Pict.views['Plansheet-BookmarksPage'].go('{~D:Record.Route~}')">{~D:Record.Label~}</a><span class="ps-bmpage-age" title="Bookmarked">{~D:Record.Age~}</span><button class="ps-bmpage-x" title="Remove" onclick="_Pict.views['Plansheet-BookmarksPage'].removeBookmark({~D:Record.IDBookmark~})">&times;</button></div>`
		},
		{
			Hash: 'BookmarksPage-FavEmpty',
			Template: /*html*/`<p class="ps-bmpage-empty">No favorites yet. Pin a page from the notebook to keep it here.</p>`
		},
		{
			Hash: 'BookmarksPage-RecentList',
			Template: /*html*/`<div class="ps-bmpage-list">{~TS:BookmarksPage-RecentRow:AppData.BookmarksPage.RecentRows~}</div>`
		},
		{
			Hash: 'BookmarksPage-RecentRow',
			Template: /*html*/`<div class="ps-bmpage-row"><a class="ps-bmpage-link" onclick="_Pict.views['Plansheet-BookmarksPage'].go('{~D:Record.Route~}')">{~D:Record.Label~}</a><span class="ps-bmpage-count ps-bmpage-count-recent" title="Recent visits">{~D:Record.CountLabel~}</span><span class="ps-bmpage-age" title="Last visited">{~D:Record.Age~}</span></div>`
		},
		{
			Hash: 'BookmarksPage-RecentEmpty',
			Template: /*html*/`<p class="ps-bmpage-empty">No history yet. Pages you visit show up here.</p>`
		},
		{
			Hash: 'BookmarksPage-TrendList',
			Template: /*html*/`<div class="ps-bmpage-list">{~TS:BookmarksPage-TrendRow:AppData.BookmarksPage.TrendRows~}</div>`
		},
		{
			Hash: 'BookmarksPage-TrendRow',
			Template: /*html*/`<div class="ps-bmpage-row"><a class="ps-bmpage-link" onclick="_Pict.views['Plansheet-BookmarksPage'].go('{~D:Record.Route~}')">{~D:Record.Label~}</a><span class="ps-bmpage-count" title="Visits in the last few days">{~D:Record.Count~}</span></div>`
		},
		{
			Hash: 'BookmarksPage-TrendEmpty',
			Template: /*html*/`<p class="ps-bmpage-empty">Nothing trending yet. Your most-visited pages will show up here.</p>`
		}
	],
	Renderables:
	[
		{
			RenderableHash: 'Plansheet-BookmarksPage',
			TemplateHash: 'Plansheet-BookmarksPage',
			ContentDestinationAddress: '#Plansheet-Workspace',
			RenderMethod: 'replace'
		}
	]
};
